import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface ClarificationRow {
  readonly clarificationId: string;
  readonly reviewId: string;
  readonly clarificationTypeCode: string;
  readonly assignedToName: string | null;
  readonly assignedToCode: string | null;
  readonly questionText: string;
  readonly responseText: string | null;
  readonly status: string;
  readonly slaDeadlineAt: string | null;
  readonly createdAt: string;
  readonly respondedAt: string | null;
}

export function ShClarificationPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ClarificationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [clarType, setClarType] = useState('COST');
  const [question, setQuestion] = useState('');

  // Respond form
  const [respondId, setRespondId] = useState<string | null>(null);
  const [response, setResponse] = useState('');

  const load = useCallback(async () => {
    if (!reviewId) return;
    try {
      const r = await apiRequest<ClarificationRow[]>(`/v1/sh/exceptions/clarification/review/${encodeURIComponent(reviewId)}`, { token });
      setRows(r); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest('/v1/sh/exceptions/clarification', {
        method: 'POST', token,
        body: { reviewId, assignedToUserId: assignTo, clarificationTypeCode: clarType, questionText: question },
      });
      setInfo(t('sh.clarification.requested'));
      setShowCreate(false); setAssignTo(''); setQuestion('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, assignTo, clarType, question, token, busy, load]);

  const handleRespond = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!respondId || busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/exceptions/clarification/${encodeURIComponent(respondId)}/respond`, {
        method: 'POST', token, body: { responseText: response },
      });
      setInfo(t('sh.clarification.responseRecorded'));
      setRespondId(null); setResponse('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [respondId, response, token, busy, load]);

  return (
    <main aria-label="sh-clarification">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to={`/sh/review/${reviewId}`} data-testid="back-review">{t('sh.common.review')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.clarification.title')}</h1>
        <Button type="button" variant="secondary" onClick={() => setShowCreate(!showCreate)} data-testid="btn-new-clarification"
                style={{ marginLeft: 'auto' }}>{t('sh.common.newBtn')}</Button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {showCreate && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }}
                 aria-label="create-clarification-form">
          <form onSubmit={handleCreate} aria-label="clarification-create-form">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 8 }}>
              <div>
                <label htmlFor="cl-to" style={{ fontSize: 11 }}>{t('sh.clarification.assignTo')}</label>
                <input id="cl-to" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} required data-testid="input-cl-to"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="cl-type" style={{ fontSize: 11 }}>{t('sh.common.type')}</label>
                <select id="cl-type" value={clarType} onChange={(e) => setClarType(e.target.value)} data-testid="select-cl-type"
                        style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  <option value="COST">{t('sh.clarification.typeCost')}</option><option value="EXECUTION">{t('sh.clarification.typeExecution')}</option>
                  <option value="QUALITY">{t('sh.clarification.typeQuality')}</option><option value="COMPLIANCE">{t('sh.clarification.typeCompliance')}</option>
                  <option value="GENERAL">{t('sh.clarification.typeGeneral')}</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cl-q" style={{ fontSize: 11 }}>{t('sh.clarification.question')}</label>
              <textarea id="cl-q" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} required
                        data-testid="input-cl-question"
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-cl">{t('sh.clarification.submit')}</Button>
              <Button type="button" variant="danger" onClick={() => setShowCreate(false)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {respondId && (
        <section style={{ padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }}
                 aria-label="respond-clarification-form">
          <form onSubmit={handleRespond} aria-label="clarification-respond-form">
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="cl-resp" style={{ fontSize: 11 }}>{t('sh.clarification.response')}</label>
                <textarea id="cl-resp" rows={3} value={response} onChange={(e) => setResponse(e.target.value)}
                          data-testid="input-cl-response"
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
              </div>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-response">{t('sh.clarification.respond')}</Button>
              <Button type="button" variant="danger" onClick={() => setRespondId(null)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.clarification.empty')}</p>
      ) : (
        <div>
          {rows.map((r) => (
            <article key={r.clarificationId}
                     style={{ padding: 12, borderRadius: 8, marginBottom: 8, border: '1px solid var(--color-border-default)', background: 'var(--color-bg-surface)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{r.clarificationTypeCode}</span>
                <span style={{
                  fontSize: 12, padding: '2px 8px', borderRadius: 10,
                  background: r.status === 'PENDING' ? 'var(--color-warning-bg)' : r.status === 'RESPONDED' ? 'var(--color-success-bg)' : r.status === 'EXPIRED' ? 'var(--color-danger-bg)' : 'var(--color-bg-muted)',
                  color: r.status === 'PENDING' ? 'var(--color-warning)' : r.status === 'RESPONDED' ? 'var(--color-success)' : r.status === 'EXPIRED' ? 'var(--color-danger)' : 'var(--color-text-muted)',
                }}>{r.status}</span>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>{t('sh.clarification.q')}</strong> {r.questionText}
              </div>
              {r.responseText && (
                <div style={{ marginBottom: 4, color: 'var(--color-success)' }}>
                  <strong>{t('sh.clarification.a')}</strong> {r.responseText}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                {t('sh.clarification.meta', { user: personLabel(r.assignedToName, r.assignedToCode) ?? '—', created: at(r.createdAt) })}
                {r.slaDeadlineAt && t('sh.clarification.slaSuffix', { sla: at(r.slaDeadlineAt) })}
              </div>
              {r.status === 'PENDING' && (
                <Button type="button" variant="secondary" onClick={() => { setRespondId(r.clarificationId); setResponse(''); }}
                        data-testid={`btn-respond-${r.clarificationId}`}
                        style={{ marginTop: 8 }}>{t('sh.clarification.respond')}</Button>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
